// Branded error pages — replaces React Router's default developer error UI
// and the bare browser 404. Used as `errorElement` on the root route, as a
// catch-all `path: "*"` 404, and reachable directly for 503 / API-down.
//
// Three pages share the same centered-card layout (matches the auth pages
// style) so the experience feels intentional rather than the generic
// "💿 Hey developer 👋" router screen or a stack-trace dump.

import { useEffect } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useRouteError,
  isRouteErrorResponse,
} from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useDict } from "@/i18n/LocaleProvider";
import { useAuthStore } from "@/stores/authStore";

// ──────────────────────────────────────────────────────────────
// Shared layout — branded center-card chrome.
// ──────────────────────────────────────────────────────────────

function ErrorLayout({
  code,
  title,
  message,
  primary,
  secondary,
}: {
  code: string;
  title: string;
  message: string;
  primary: { label: string; onClick: () => void };
  secondary?: { label: string; to: string };
}) {
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-5">
      <div className="w-full max-w-[480px]">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="TrueHacking" className="h-20 w-auto mb-4 mx-auto" />
          <div className="font-display text-[32px] font-bold text-accent tracking-[0.14em]">
            TRUEHACKING
          </div>
        </div>

        <Card className="!p-5 sm:!p-7">
          <div className="font-mono text-[11px] text-accent uppercase tracking-[0.2em] mb-3">
            {code}
          </div>
          <div className="font-display text-[22px] sm:text-[26px] font-bold text-content leading-[1.2] mb-3">
            {title}
          </div>
          <div className="font-sans text-[14px] text-muted leading-[1.6] mb-6">
            {message}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="primary" onClick={primary.onClick}>
              {primary.label}
            </Button>
            {secondary && (
              <Link to={secondary.to} className="no-underline">
                <Button variant="default" full>
                  {secondary.label}
                </Button>
              </Link>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function homePathFor(isAuthed: boolean): string {
  return isAuthed ? "/app" : "/";
}

// ──────────────────────────────────────────────────────────────
// 404 — page not found
// ──────────────────────────────────────────────────────────────

export function NotFoundPage() {
  const navigate = useNavigate();
  const t = useDict().app.errors;
  const isAuthed = Boolean(useAuthStore((s) => s.accessToken));
  const homePath = homePathFor(isAuthed);

  return (
    <ErrorLayout
      code={t.notFoundCode}
      title={t.notFoundTitle}
      message={t.notFoundMsg}
      primary={{ label: t.goHome, onClick: () => navigate(homePath) }}
      secondary={isAuthed ? { label: t.openDashboard, to: "/app" } : undefined}
    />
  );
}

// ──────────────────────────────────────────────────────────────
// 503 — service unavailable / API down
// ──────────────────────────────────────────────────────────────

// /health recovery polling: every 5 s the page checks whether the
// backend is reachable again. As soon as it answers any 2xx, we
// navigate the user back to where they were (carried in route state
// by useApiHealthMonitor) — or to home as a fallback. Raw fetch is
// used (not the queryClient/api helper) so the recovery probe doesn't
// itself feed back into the global health monitor.
const HEALTH_POLL_INTERVAL_MS = 5_000;

export function ServiceUnavailablePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useDict().app.errors;
  const isAuthed = Boolean(useAuthStore((s) => s.accessToken));
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const fallback = isAuthed ? "/app" : "/";

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const res = await fetch("/health", { cache: "no-store" });
        if (cancelled) return;
        if (res.ok) {
          // Use replace so the /503 page doesn't pollute history.
          navigate(returnTo ?? fallback, { replace: true });
        }
      } catch {
        // Network still unreachable — keep polling silently.
      }
    }
    void poll(); // immediate first attempt — don't make the user wait 5s
    const id = setInterval(poll, HEALTH_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [navigate, returnTo, fallback]);

  return (
    <ErrorLayout
      code={t.unavailableCode}
      title={t.unavailableTitle}
      message={t.unavailableMsg}
      primary={{ label: t.tryNow, onClick: () => window.location.reload() }}
      secondary={
        isAuthed
          ? { label: t.backDashboard, to: "/app" }
          : { label: t.backHome, to: "/" }
      }
    />
  );
}

// ──────────────────────────────────────────────────────────────
// Generic runtime error — used as `errorElement` on the route tree.
// Caught here: render-time errors thrown by route components, errors
// thrown by loaders/actions (we don't use those yet), and Response
// objects rejected from a loader (also unused).
// ──────────────────────────────────────────────────────────────

export function RouteErrorPage() {
  const t = useDict().app.errors;
  const isAuthed = Boolean(useAuthStore((s) => s.accessToken));
  const error = useRouteError();

  // Error details go to the browser console (visible in DevTools for
  // devs, invisible to end users). Never render them on the page —
  // they can leak stack frames, file paths, or upstream messages that
  // are not safe to surface. Hook runs before any early returns so
  // it is called unconditionally on every render.
  useEffect(() => {
    console.error("[RouteErrorPage] uncaught route error:", error);
  }, [error]);

  // React Router throws a Response for 404s on data routes — handle that
  // dedicated path so it shows the 404 page, not the generic error one.
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />;
  }
  if (isRouteErrorResponse(error) && error.status === 503) {
    return <ServiceUnavailablePage />;
  }

  return (
    <ErrorLayout
      code={t.errorCode}
      title={t.errorTitle}
      message={t.errorMsg}
      primary={{ label: t.reload, onClick: () => window.location.reload() }}
      secondary={isAuthed ? { label: t.backDashboard, to: "/app" } : { label: t.backHome, to: "/" }}
    />
  );
}
